/** Heroes on the adventure map: facing, idle, and the walk.
 *
 * A hero is drawn through the same object layer as everything else, because it
 * has to depth-sort against towns and trees. What this module owns is the
 * hero's *motion*: the observation says where a hero is, but a hero that
 * teleports between cells when a move resolves looks broken, so a `HeroMoved`
 * event is played as an actual walk and the sprite is drawn from an
 * interpolated world position until it arrives.
 *
 * The renderer decides nothing here (I10). The kernel has already resolved
 * where the hero ended up and what it cost; the walk is a presentation of that
 * result, and if it is interrupted the sprite snaps to the truth.
 *
 * The art ships one three-quarter view plus a left-facing mirror with the key
 * light re-baked, so the eight headings collapse onto two sprites - see
 * `keys.ts`. Flipping in code instead would put the highlight on the shadowed
 * side, which is worse than having six of the eight headings share a pose.
 */

import type { Atlas } from "../atlas.js";
import { TILE_SIZE, cellGround, cellPoint } from "../coords.js";
import {
  IDLE_FRAME_COUNT, directionalKey, facingFromDelta, factionOfHeroDef, heroSpriteKey, idleKey,
} from "./keys.js";

export { facingFromDelta, factionOfHeroDef } from "./keys.js";
import type { WorldSprite } from "./objects.js";
import { LAYER_HERO, makeWorldSprite, resolveOwned } from "./objects.js";
import { hash32 } from "./scatter.js";

/**
 * The owner standard, carried on the hero's own tile.
 *
 * A hero is one tile of sprite on a map full of one-tile scenery, so the thing
 * that finds him is the standard, not the figure - which is why the classic flies one
 * on every hero on the map. Two placements have been wrong for opposite
 * reasons and the third is the one that reads:
 *
 *  - Pinned inside the sprite box at (0.10, 0.74) the banner crossed the
 *    rider's chest and the horse's head, and neither silhouette survived.
 *  - Moved clear of the box at -0.26 it stopped being his: the pole stood a
 *    full tile west on open grass with the rider on cobble beside it, and a
 *    player scanning the map saw a flagged something and a mounted something.
 *
 * So it is planted just inside the sprite's own footprint - the pole foot sits
 * on the hero's ground line, a couple of pixels in from the box's left edge -
 * and `flagBehind` draws it UNDER him. The pole rises behind the horse's
 * shoulder and only the cloth clears the figure, which is one object with a
 * colour on it rather than two objects sharing a cell. The height is cut with
 * it: a standard as tall as the rider competes with him.
 */
const HERO_FLAG_PX = 42;
const HERO_FLAG_AT_X = 0.02;
const HERO_FLAG_AT_Y = 0.99;
/** Of `obj.bld_rally_flag`, the pole and the cloth; below this is the cairn. */
const RALLY_FLAG_CROP = 0.62;

/** Milliseconds per idle frame at rest, and while walking. */
const IDLE_PERIOD_MS = 260;
const WALK_PERIOD_MS = 110;
/** Default milliseconds to cross one cell. Diagonals take the same time, as in the classic. */
export const DEFAULT_STEP_MS = 150;

export interface HeroSnapshot {
  readonly id: string;
  /** `HeroDefId`, `<faction>_<name>`; the faction picks the adventure sprite. */
  readonly def: string;
  readonly owner: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** 0..7 clockwise from north. */
  readonly facing: number;
}

interface HeroRender {
  snapshot: HeroSnapshot;
  colour: string | null;
  /** Interpolated world pixel of the ground line. */
  gx: number;
  gy: number;
  facing: number;
  walk: WalkState | null;
}

interface WalkState {
  readonly points: readonly { readonly gx: number; readonly gy: number }[];
  readonly msPerStep: number;
  readonly done: () => void;
  elapsed: number;
}

export class HeroDirector {
  private readonly heroes = new Map<string, HeroRender>();
  private clock = 0;

  constructor(private readonly atlas: Atlas) {}

  /** Adopt the observation. Heroes mid-walk keep their interpolated position. */
  sync(snapshots: readonly HeroSnapshot[], colourOf: (owner: string) => string | null): void {
    const live = new Set<string>();
    for (const s of snapshots) {
      live.add(s.id);
      const existing = this.heroes.get(s.id);
      if (existing === undefined) {
        const g = cellGround(cellPoint(s.x, s.y, s.z));
        this.heroes.set(s.id, {
          snapshot: s,
          colour: colourOf(s.owner),
          gx: g.x as number,
          gy: g.y as number,
          facing: s.facing,
          walk: null,
        });
        continue;
      }
      existing.snapshot = s;
      existing.colour = colourOf(s.owner);
      if (existing.walk === null) {
        const g = cellGround(cellPoint(s.x, s.y, s.z));
        existing.gx = g.x as number;
        existing.gy = g.y as number;
        existing.facing = s.facing;
      }
    }
    for (const id of [...this.heroes.keys()]) {
      if (live.has(id)) continue;
      // A hero that left the observation - defeated, or walked out of sight -
      // must not leave an awaited walk hanging.
      this.heroes.get(id)?.walk?.done();
      this.heroes.delete(id);
    }
  }

  /**
   * Queue a walk along `path`, a run of cells starting at the hero's current
   * cell. The promise resolves when the hero has actually arrived.
   *
   * Resolving on arrival rather than on a wall-clock timer matters: the walk is
   * advanced by frame deltas clamped to 64 ms, so on a slow frame the animation
   * runs behind the clock. A timer would report the move finished while the
   * sprite was still two cells short, and the next command would snap it.
   */
  startWalk(
    heroId: string,
    path: readonly { readonly x: number; readonly y: number; readonly z: number }[],
    msPerStep = DEFAULT_STEP_MS,
  ): Promise<void> {
    const h = this.heroes.get(heroId);
    if (h === undefined || path.length < 2) return Promise.resolve();
    const points = path.map((c) => {
      const g = cellGround(cellPoint(c.x, c.y, c.z));
      return { gx: g.x as number, gy: g.y as number };
    });
    // A walk already running on this hero is superseded, not queued.
    h.walk?.done();
    return new Promise<void>((resolve) => {
      h.walk = { points, msPerStep, done: resolve, elapsed: 0 };
    });
  }

  /** True while any hero is still moving. */
  get walking(): boolean {
    for (const h of this.heroes.values()) if (h.walk !== null) return true;
    return false;
  }

  /** Drop every walk and snap to the observation. Used when an animation aborts. */
  settle(): void {
    for (const h of this.heroes.values()) {
      if (h.walk === null) continue;
      h.walk.done();
      h.walk = null;
      const g = cellGround(cellPoint(h.snapshot.x, h.snapshot.y, h.snapshot.z));
      h.gx = g.x as number;
      h.gy = g.y as number;
      h.facing = h.snapshot.facing;
    }
  }

  advance(dtMs: number): void {
    this.clock += dtMs;
    for (const h of this.heroes.values()) {
      const w = h.walk;
      if (w === null) continue;
      w.elapsed += dtMs;
      const total = w.msPerStep * (w.points.length - 1);
      if (w.elapsed >= total) {
        const last = w.points[w.points.length - 1]!;
        h.gx = last.gx;
        h.gy = last.gy;
        h.walk = null;
        h.facing = h.snapshot.facing;
        w.done();
        continue;
      }
      const t = w.elapsed / w.msPerStep;
      const i = Math.floor(t);
      const f = t - i;
      const a = w.points[i]!;
      const b = w.points[i + 1] ?? a;
      h.gx = a.gx + (b.gx - a.gx) * f;
      h.gy = a.gy + (b.gy - a.gy) * f;
      h.facing = facingFromDelta(Math.sign(b.gx - a.gx), Math.sign(b.gy - a.gy));
    }
  }

  /** The world position a camera following a hero should look at. */
  centreOf(heroId: string): { readonly x: number; readonly y: number } | null {
    const h = this.heroes.get(heroId);
    if (h === undefined) return null;
    return { x: h.gx, y: h.gy - TILE_SIZE / 2 };
  }

  cellOf(heroId: string): { readonly x: number; readonly y: number; readonly z: number } | null {
    const h = this.heroes.get(heroId);
    return h === undefined
      ? null
      : { x: h.snapshot.x, y: h.snapshot.y, z: h.snapshot.z };
  }

  /** Sprites for the current instant, ready for the object layer. */
  sprites(z: number, animate = true): WorldSprite[] {
    const out: WorldSprite[] = [];
    for (const h of this.heroes.values()) {
      if (h.snapshot.z !== z) continue;
      const base = heroSpriteKey(factionOfHeroDef(h.snapshot.def));
      const period = h.walk !== null ? WALK_PERIOD_MS : IDLE_PERIOD_MS;
      // Phase-shift by the hero id: a row of heroes breathing in lockstep is a
      // giveaway that the animation is driven by a global clock.
      const phase = hash32(0, 0, hashString(h.snapshot.id)) % IDLE_FRAME_COUNT;
      const frame = animate
        ? Math.floor(this.clock / period) + phase
        : 0;
      const key = idleKey(directionalKey(base, h.facing), frame);
      const resolved = this.atlas.has(key) ? key : directionalKey(base, h.facing);
      const owned = heroOwnerMark(this.atlas, resolved, h.colour);

      const s = makeWorldSprite(this.atlas, {
        id: `hero:${h.snapshot.id}`,
        key: owned.key,
        groundX: h.gx,
        groundY: h.gy,
        layer: LAYER_HERO,
        flagKey: owned.flagKey,
        // A hero is one tile of sprite on a map full of one-tile scenery, so
        // the thing that finds him is the standard, not the figure. The generic
        // rule sizes a pennant at a sixth of what it flies over, which on a
        // 97px rider is a 16px scrap invisible against grass. The classic flies a
        // full banner beside the horse for exactly this reason.
        flagHeight: HERO_FLAG_PX,
        flagAtX: HERO_FLAG_AT_X,
        flagAtY: HERO_FLAG_AT_Y,
        flagCrop: RALLY_FLAG_CROP,
        flagBehind: true,
        // `resolveOwned` already picked white for a baked owner variant and the
        // player tint for the neutral banner. Re-tinting a baked banner would
        // multiply blue by blue.
        flagTint: owned.flagTint,
      });
      if (s !== null) out.push(s);
    }
    return out;
  }
}

/**
 * The hero's owner mark.
 *
 * `resolveOwned` reaches for `obj.pu_banner`, which is a heap of captured
 * pennants lying on the ground - right for a pickup, wrong for a rider, and at
 * any placement beside a hero it reads as a red smear rather than as a colour.
 * `obj.bld_rally_flag` is the one upright standard in the set and it ships the
 * eight baked owner variants, so a hero flies that instead, cropped above its
 * cairn by `flagCrop`.
 */
function heroOwnerMark(
  atlas: Atlas, spriteKey: string, colour: string | null,
): { readonly key: string; readonly flagKey: string | null; readonly flagTint: number } {
  const owned = resolveOwned(atlas, spriteKey, colour);
  if (owned.flagKey === null) return owned;
  const baked = colour === null ? null : `${RALLY_FLAG_KEY}.owner.${colour}`;
  if (baked !== null && atlas.has(baked)) {
    return { key: owned.key, flagKey: baked, flagTint: 0xffffff };
  }
  if (atlas.has(RALLY_FLAG_KEY)) {
    return { key: owned.key, flagKey: RALLY_FLAG_KEY, flagTint: owned.flagTint };
  }
  return owned;
}

const RALLY_FLAG_KEY = "obj.bld_rally_flag";

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return h;
}
